import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import hubLayout from 'styles/css/hubLayout.module.css';
import { ACCESS_TOKEN_NAME, checkSmsVerification, encryptSHA256, getIdDuplicateCheck, getUserFirstLoginData, putAgreeDate, putFirstLoginUser, sendSmsForVerification } from 'api/api';
import Loading from 'components/common/Loading';
import { useModal } from 'components/common/ModalLayout';
import { logout } from 'api/bridgeUser';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { Timer } from 'components/common/Timer';
import { ServiceTerms } from './ServiceTerms';
import { PrivacyPolicy } from './PrivacyPolicy';
import { User } from 'type/type';
import { EmailInputField, MobileInputField, NameInputField, PwInputField, AddrInputComponent, PwCheckInputField } from 'components/common/InputField';
import { NiceUi, onClickNicePass } from 'components/nice/NiceUi';
import { UserLoginInfoContext } from 'pages/Layout';

const urls = {
  verification: '/user/init/verification',
  agree: '/user/init/agree',
  first_pw_change: '/user/init/first-pw-change',
  bad_request: '/user/bad-request',
};
const initPageRedirect = (url: string, navigate: Function) => { 
  navigate(url);
}
// 본인인증, 약관동의, 최초 비번 변경 체크.
const checkUserFirstLoginData = async ({
  navigate,
  showModal,
}: {
  navigate: Function;
  showModal: Function;
}) => {
  const response = await getUserFirstLoginData();
  // console.log('response:', response);
  if (response.code !== 200) {
    // console.log('response:', response);
    showModal("alert", response.message);
  } else {
    // 최초 로그인 정보에 따라 redirect.
    // 본인인증 필요시 현재 페이지, 약관동의 필요시 약관동의 페이지, 최초 비번 변경 필요시 비번 변경 페이지로 이동, 모두 완료시 안드로이드 브릿지로 메인화면 이동 호출.
    const { data } = response;
    // console.log('data:', data);
    // CAUTION: 순서 바꾸지 말것.
    if (!data.verification) {
      // 현재 페이지 계속.
    } 
    else if (!data.agree) initPageRedirect(urls.agree, navigate);
    else if (!data.first_pw_change) initPageRedirect(urls.first_pw_change, navigate);
    else {
      console.log('이미 인증된 사용자입니다.');
      logout();
    }
  }
};

export function UserVerification() {
  // ****************************** 1. props, hook
  const redirect_code = new URLSearchParams(window.location.search).get('redirect_code'); 
  const navigate = useNavigate();
  const { showModal } = useModal();
  // useForm
  const { register, formState: { errors } } = useForm<any>({
    mode: 'all',
  });
  
  // ****************************** 2. state
  const [loading, setLoading] = useState(false);
  // 사용자의 동작에 따라 화면을 변경하기 위한 state
  const defaultUiManage = {
    selectVerificationType: 'SMS',
    nameInput: false,
    mobileInput: false,
    sendSmsInput: false,
    verfyNumberInput: false,
  };
  const [uiManage, setUiManage] = useState(defaultUiManage);
  const defaultSmsUserData = {
    name: '',
    mobile: '',
    verify_number: '',
    expire_date: null,
  };
  const [smsUserData, setSmsUserData] = useState(defaultSmsUserData);
  // TODO 뒤로가기 방지하기.

  // ****************************** 3. api
  const mainApi = async () => { 
    setLoading(false);

    await checkUserFirstLoginData({
      navigate,
      showModal,
    });

    setLoading(true);
  };

  // 인증번호 발송
  const checkUserAndSendSms = async () => {
    setLoading(false);
    const response = await sendSmsForVerification(smsUserData);
    // console.log('response:', response);

    if (response.code >= 500) {
      showModal("alert", response.code+' '+response.message);
    } 
    else if (response.code !== 200) {
      showModal("alert", response.message);
    } else {
      // TODO TODO 추후 인증번호 발송에 대한 알람은 
      // showModal("alert", '인증번호를 발송했습니다.');

      setSmsUserData({ ...smsUserData, expire_date: response.data.expire_date });
      setUiManage({ ...uiManage, sendSmsInput: true });
    }

      setLoading(true);
  };

  // 인증번호 확인.
  const checkSms = async () => {
    setLoading(false);
    const response = await checkSmsVerification(smsUserData);
    // console.log('response:', response);

    if (response.code >= 500) {
      showModal("alert", response.code+' '+response.message);
    } 
    else if (response.code !== 200) {
      showModal("alert", response.message + '\n새로운 인증번호를 받아주세요.');
      setSmsUserData({ ...smsUserData, verify_number: '', expire_date: null });
    } else {
      if (!response.data.verify) {
        showModal("alert", `인증번호가 맞지 않습니다.\n${response.data.try_count}회 시도하셨고, ${response.data.max_try_count}회가 될 경우 새로운 인증번호를 받아주세요.`);
      } else {
        // 약관동의로 넘어가기.
        initPageRedirect(urls.agree, navigate);
      }
    }

    setLoading(true);
  };

  // ****************************** 4. event handler
  const handleChangeSelectVerificationType = () => {
    // 완전 초기화 방법
    // setUiManage(defaultUiManage);
    // setSmsUserData(defaultSmsUserData);

    // 값은 보존하고 방법만 초기화하는 방법
    setUiManage({ ...uiManage, selectVerificationType: '' });
  }

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setSmsUserData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }

  const handleCheckSmsDisable = () => { 
    // console.log('smsUserData', smsUserData);
    return smsUserData.verify_number.length === 0
      || smsUserData.expire_date === null
      || dayjs(smsUserData.expire_date).diff(dayjs().add(9, 'h'), 's') < 0;
  }

  // ****************************** 5. useEffect
  useEffect(() => {
    mainApi();
    
    // NicePass 인증 결과 처리
    if (redirect_code === 'user_not_match') {
      showModal("alert", '등록된 사용자 정보와 일치하지 않습니다.');
    }
  }, []);

  return (
    <>
      {!loading ? <Loading /> : 
        <>
          <div className={`${hubLayout.cnt_box}`} style={{overflowY: "auto"}}>
            <div className={`${hubLayout.acc__cnt}`}>
              <div className={`${hubLayout.sec_title} ${hubLayout.fixed_title}`}>
                <h2>본인인증</h2>
              </div>
              {!uiManage.selectVerificationType &&
                <div className={`${hubLayout.acc_total}`} style={{ margin: '4rem 0 20px', textAlign: 'center' }}>
                  <div>
                    <p>처음 방문하신 고객님</p>
                    <p>환영합니다.</p>
                    <p>안전한 사용을 위해</p>
                    <p>본인인증을 진행합니다.</p>
                  </div>
                  <br />
                  <div>
                    {/* <p><button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={() => setUiManage({ ...uiManage, selectVerificationType: 'SMS' })}>SMS인증</button></p>
                    <br /> */}
                    <p><button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={() => onClickNicePass({ type:'init_verification', redirect_url: {success: urls.agree, fail: urls.verification}, access_token: localStorage.getItem(ACCESS_TOKEN_NAME) as string })}>PASS인증</button></p>
                  </div>
                </div>
              }
              <div style={{display:'none'}}>
                <NiceUi />
              </div>

              {/* 이하 미사용하는 SMS 인증 방법. */}
              {uiManage.selectVerificationType === 'SMS' &&
                <div className={`${hubLayout.acc_total}`} style={{ margin: '4rem 0 20px', textAlign: 'center' }}>
                  <div>
                    <p>본인인증을 진행해주세요.</p>
                  </div>
                  <br />
                  <div className={`${hubLayout.acc_item_info}`}>
                    <dl>
                      <dt>이름</dt>
                      <dd>
                        <input type="text" className={`${errors.name && hubLayout.border_red}`}
                          placeholder="홍길동" value={smsUserData.name} 
                          {...register(`name`, {
                            required: true,
                            onChange: (e: any) => { handleChange(e) },
                            onBlur: () => { setUiManage({ ...uiManage, nameInput: true }) },
                          })}
                        />
                      </dd>
                    </dl>
                    {// uiManage.nameInput &&
                      true &&
                        <dl>
                          <dt>휴대폰 번호</dt>
                          <dd>
                            <input type="number" className={`${errors.mobile && hubLayout.border_red}`}
                              placeholder='01012345678' value={smsUserData.mobile}
                              {...register(`mobile`, {
                                required: true,
                                pattern: /^[0-9]{11}$/g,
                                maxLength: 11,
                                onChange: (e: any) => { handleChange(e) },
                                onBlur: () => { setUiManage({ ...uiManage, mobileInput: true }) },
                              })}
                            />
                          </dd>
                        </dl>
                      }
                      {// uiManage.mobileInput &&
                        true &&
                          <div>
                            <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => checkUserAndSendSms()}>인증번호 발송</button>
                          </div>
                      }
                      {// uiManage.sendSmsInput &&
                        true &&
                          <dl>
                            <dt>인증번호</dt>
                            <dd>
                              <input type="number" className={`${errors.verify_number && hubLayout.border_red}`}
                                placeholder='123456' value={smsUserData.verify_number}
                                {...register(`verify_number`, {
                                  required: true,
                                  pattern: /^[0-9]{6}$/g,
                                  maxLength: 6,
                                  onChange: (e: any) => { handleChange(e) },
                                  onBlur: () => { setUiManage({ ...uiManage, verfyNumberInput: true }) },
                                })}
                              />
                            </dd>
                            <dd>
                              <Timer leftSeconds={smsUserData.expire_date && dayjs(smsUserData.expire_date).diff(dayjs().add(9, 'h'), 's') > 0 ? dayjs(smsUserData.expire_date).diff(dayjs().add(9, 'h'), 's') : 0} />
                            </dd>
                          </dl>
                      }
                    {// uiManage.verfyNumberInput &&
                      true &&
                        <div>
                          <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.pink}`} onClick={() => checkSms()} disabled={handleCheckSmsDisable()}>인증번호 확인</button>
                        </div>
                    }
                  </div>
                </div>
              }
              {uiManage.selectVerificationType && false && 
                <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title}`}>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => handleChangeSelectVerificationType()}>인증 방식을 다시 선택</button>
                </div>
              }
            </div>
          </div>
        </>
      }
    </>
  );
}

export function UserAgree() {
  // ****************************** 1. props, hook
  const navigate = useNavigate();
  const { showModal } = useModal();

  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isPiChecked, setIsPiChecked] = useState(false);
  const [isInfoChecked, setIsInfoChecked] = useState(false);
  const [isPiAgreeVisible, setIsPiAgreeVisible] = useState(true);
  const [isInfoPiAgreeVisible, setIsInfoPiAgreeVisible] = useState(true);
  
  // ****************************** 2. state
  const [loading, setLoading] = useState(false);

  // TODO 뒤로가기 방지하기.
  // ****************************** 3. api
  const mainApi = async () => { 
    setLoading(false);
    await checkUserFirstLoginData({
      navigate,
      showModal,
    });
    setLoading(true);
  };

  const handleAllCheck = (checked: boolean) => {
    setIsAllChecked(checked);
    setIsPiChecked(checked);
    setIsInfoChecked(checked);
  };

  const handleIndividualCheck = (key: "pi" | "info", checked: boolean) => {
    if (key === "pi") {
      setIsPiChecked(checked);
      setIsAllChecked(checked && isInfoChecked);
    } else if (key === "info") {
      setIsInfoChecked(checked);
      setIsAllChecked(checked && isPiChecked);
    }
  };

  const goEdit = async () => {
    const response = await putAgreeDate();
    if (response.code === 200) {
      initPageRedirect(urls.first_pw_change, navigate);
    } else {
      showModal('alert', '여기냐'+response.message)
    }
  };

  // ****************************** 5. useEffect
  useEffect(() => {
    mainApi();
  }, []);
  return (
    <>
      {!loading ? <Loading /> : 
        <>
          <div className={`${hubLayout.cnt_box}`} style={{overflowY: "auto"}}>
            <div className={`${hubLayout.acc__cnt}`}>
              <div className={`${hubLayout.sec_title} ${hubLayout.fixed_title}`}>
                <h2>약관동의</h2>
              </div>
              <ul className={`${hubLayout.inp_list} ${hubLayout.terms}`} style={{ marginTop: "6rem" }}>
                <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
                  <div className={`${hubLayout.pi_agree}`}>
                    <input type="checkbox" id="chk-all-pi-agree" className={`${hubLayout.check}`} checked={isAllChecked} onChange={(e) => handleAllCheck(e.target.checked)} />
                    <label htmlFor="chk-all-pi-agree">전체 동의</label>
                  </div>
                </li>
                <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
                  <div className={`${hubLayout.pi_agree}`}>
                    <input type="checkbox" id="chk-pi-agree" className={`${hubLayout.check}`} checked={isPiChecked} onChange={(e) => handleIndividualCheck("pi", e.target.checked)} />
                    <label htmlFor="chk-pi-agree">(필수)회원가입</label>
                    <button type="button" className={`${hubLayout.btn_tx_under}`} onClick={() => setIsPiAgreeVisible((prev) => !prev)}>전문보기</button>
                  </div>
                  <div className={`${hubLayout.term_info} ${isPiAgreeVisible ? hubLayout.hide : ''}`}>
                    <ServiceTerms />
                  </div>
                </li>
                <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
                  <div className={`${hubLayout.pi_agree}`}>
                    <input type="checkbox" id="chk-info-pi-agree" className={`${hubLayout.check}`} checked={isInfoChecked} onChange={(e) => handleIndividualCheck("info", e.target.checked)} />
                    <label htmlFor="chk-info-pi-agree">(필수)개인정보약관</label>
                    <button type="button" className={`${hubLayout.btn_tx_under}`} onClick={() => setIsInfoPiAgreeVisible((prev) => !prev)}>전문보기</button>
                  </div>
                  <div className={`${hubLayout.term_info} ${isInfoPiAgreeVisible ? hubLayout.hide : ''}`}>
                    <PrivacyPolicy />
                  </div>
                </li>
              </ul>
              <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={goEdit} disabled={!isAllChecked}>다음</button>
              </div>
            </div>
          </div>
        </>
      }
    </>
  );
}

export function UserFirstPwChange() {
  // ****************************** 1. props, hook
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { userLoginInfo } = useContext(UserLoginInfoContext);
  const [isDuplicateCheck, setIsDuplicateCheck] = useState(false);
  const [user, setUser] = useState<User>(userLoginInfo ? userLoginInfo : Object)
  // useForm
  const { register, formState: { errors, isValid }, setValue } = useForm<any>({
    mode: 'all',
  });

  // ****************************** 2. state
  const [loading, setLoading] = useState(false);
  // ****************************** 3. api
  const mainApi = async () => { 
    if (!userLoginInfo) {
      return showModal("alert", '잘못된 접근입니다.', { confirm: true, cancel: true },
        logout,
        logout,
      );
    }
    setLoading(true);
    user.id = '';
    await checkUserFirstLoginData({ navigate, showModal });
    setLoading(false);
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    if (name === 'id') setIsDuplicateCheck(false);
    setUser((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const updateUser = async () => {
    const { password, passwordcheck } = user;
  
    if (!password || !passwordcheck) {
      return showModal('alert', '비밀번호를 입력해주세요.');
    }
  
    if (password !== passwordcheck) {
      return showModal('alert', '비밀번호 확인을 동일하게 입력해주세요.');
    }
  
    if (!isDuplicateCheck) {
      return showModal('alert', 'ID 중복체크를 해주세요.');
    }
  
    try {
      const encryptedPwd = encryptSHA256(password);
      const userPayload = { ...user, password: encryptedPwd };
  
      const userRes = await putFirstLoginUser(userPayload);
      if (userRes.code === 200) {
        return showModal(
          'alert',
          '회원정보 수정이 완료되었습니다. 다시 로그인 후 이용해주세요.',
          { confirm: true, cancel: true },
          logout,
          logout
        );
      }
  
      return showModal('alert', userRes.message);
    } catch (error) {
      console.error('회원정보 수정 오류:', error);
      return showModal('alert', '회원정보 수정 중 오류가 발생했습니다.');
    }
  };

  const duplicateCheck = async () => {
    setLoading(true);
    try {
      if (!user.id) {
        return showModal('alert', 'ID를 입력해주세요.');
      }
      const response = await getIdDuplicateCheck(user.id);
      if (response.code === 200) {
        showModal('alert', `'${user.id}'는 사용가능한 아이디입니다.`);
        setIsDuplicateCheck(true);
      } else {
        showModal('alert', `이미 사용중인 아이디입니다. 다시 입력해주세요.`);
        setIsDuplicateCheck(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  // ****************************** 5. useEffect
  useEffect(() => {
    mainApi();
  }, []);
  return (
    <>
      {loading ? <Loading /> : 
        <>
          <div id="wrap">
            <div className={`${hubLayout.cnt_box}`}>
              <div className={`${hubLayout.acc__cnt}`}>
                <div className={`${hubLayout.sec_title} ${hubLayout.fixed_title}`}>
                  <h2>최초 로그인 회원정보 변경</h2>
                </div>
                <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`} style={{ marginTop: "4rem" }}>기본 정보</h2>
                  <ul className={`${hubLayout.inp_list}`}>
                    <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
                      <label className={`${hubLayout.imp}`}>ID</label>
                      <div className={`${hubLayout.display_flex}`}>
                        <input type='text' placeholder='영문, 숫자 4~20자' className={`${errors.id && hubLayout.border_red}`} value={user.id}
                          {...register(`id`, {
                            required: true,
                            pattern: /^[a-z][0-9a-z]{3,19}$/g,
                            onChange: handleChange
                          })}
                        />
                        <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} style={{ width: "100px" }} onClick={duplicateCheck} disabled={errors.id ? true : false}>중복체크</button>
                      </div>
                      <p className={`${errors.id && hubLayout.tx_red}`}>* 아이디는 영문 소문자로 시작하며, 4~20자의 영문 소문자와 숫자로만 입력 가능합니다.</p>
                    </li>
                    <PwInputField value={user} register={register} registerOptions={{ required: true, onChange: handleChange }} errors={errors} />
                    <PwCheckInputField value={user} register={register} registerOptions={{ required: false, onChange: handleChange }} errors={errors} />
                    <NameInputField value={user} register={register} registerOptions={{ required: true }} errors={errors} readOnly />
                    <MobileInputField value={user} register={register} registerOptions={{ required: true }} errors={errors} readOnly />
                    <EmailInputField value={user} register={register} registerOptions={{ required: true, onChange: handleChange }} errors={errors} />
                    <AddrInputComponent name={'address'} title={'주소'} user={user} handleChange={handleChange} register={register} errors={errors} setValue={setValue} />
                  </ul>
                  <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={updateUser} disabled={!isValid}>수정</button>
                  </div>
              </div>
            </div>
          </div>
        </>
      }
    </>
  );
}